import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CashFlowRequestOptions } from '@/lib/cashFlow';
import {
  addKmdManualPayment,
  deleteKmdManualPayment,
  setKmdFinalized,
  updateKmdManualPayment,
  type AddKmdPaymentInput,
  type UpdateKmdPaymentInput,
} from '@/lib/kmdSettlements';

function useRefreshKmd() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
    void queryClient.invalidateQueries({ queryKey: ['bank'] });
    void queryClient.invalidateQueries({ queryKey: ['kmd-actuals'] });
  };
}

export function useAddKmdManualPayment(options?: CashFlowRequestOptions) {
  const refresh = useRefreshKmd();
  return useMutation({
    mutationFn: ({ entryId, input }: { entryId: string; input: AddKmdPaymentInput }) =>
      addKmdManualPayment(entryId, input, options),
    onSuccess: refresh,
  });
}

export function useDeleteKmdManualPayment(options?: CashFlowRequestOptions) {
  const refresh = useRefreshKmd();
  return useMutation({
    mutationFn: (paymentId: string) => deleteKmdManualPayment(paymentId, options),
    onSuccess: refresh,
  });
}

export function useUpdateKmdManualPayment(options?: CashFlowRequestOptions) {
  const refresh = useRefreshKmd();
  return useMutation({
    mutationFn: ({ paymentId, input }: { paymentId: string; input: UpdateKmdPaymentInput }) =>
      updateKmdManualPayment(paymentId, input, options),
    onSuccess: refresh,
  });
}

export function useSetKmdFinalized(options?: CashFlowRequestOptions) {
  const refresh = useRefreshKmd();
  return useMutation({
    mutationFn: ({ entryId, finalized }: { entryId: string; finalized: boolean }) =>
      setKmdFinalized(entryId, finalized, options),
    onSuccess: refresh,
  });
}